import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiActivity,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiArchive,
  FiSearch,
  FiEye,
  FiX,
} from "react-icons/fi";
import reconciliationService from "../../services/reconciliationService";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

export default function SuperAdminCashRadar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [drilldownHandovers, setDrilldownHandovers] = useState([]);
  const [drilldownDeposits, setDrilldownDeposits] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reconciliationService.getSuperAdminRadar();
      setData(res.data);
    } catch (err) {
      toast.error(err.message || "Failed to load cross-college cash radar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCollegeDrilldown = async (collegeItem) => {
    setSelectedCollege(collegeItem);
    setDrilldownLoading(true);
    try {
      const colId = collegeItem.college?._id || collegeItem.college;
      const [hRes, dRes] = await Promise.allSettled([
        reconciliationService.getPendingHandovers({ college: colId }),
        reconciliationService.getDeposits({ college: colId }),
      ]);

      if (hRes.status === "fulfilled") setDrilldownHandovers(hRes.value.data || []);
      if (dRes.status === "fulfilled") setDrilldownDeposits(dRes.value.data || []);
    } catch (err) {
      toast.error("Failed to load college drilldown details");
    } finally {
      setDrilldownLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner" />
        Loading Super Admin Cross-College Cash Radar…
      </div>
    );
  }

  const grand = data?.grandTotals || {};
  const colleges = data?.colleges || [];

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header" style={{ marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 className="page-title">Cross-College Cash Radar Matrix 🌐</h1>
          <p className="page-subtitle">
            Centralized multi-tenant oversight of cash collections, clerk shortages, vault holdings, and bank deposits across all colleges.
          </p>
        </div>
        <div>
          <button className="btn btn-outline" onClick={loadData}>
            <FiRefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ── High Level Aggregates ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: "0.85rem",
          marginBottom: "1.5rem",
        }}
      >
        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #003b67" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Total Cash Handed Over
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#003b67", marginTop: 4 }}>
            {inr(grand.totalHandedOver)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Expected: {inr(grand.totalExpected)}
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #16a34a" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Deposited into Banks
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#16a34a", marginTop: 4 }}>
            {inr(grand.totalBankDeposited)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Central consolidated banking
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #f59e0b" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Cash in College Vaults
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#f59e0b", marginTop: 4 }}>
            {inr(grand.cashInVault)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Physical cash awaiting bank deposit
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #ef4444" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Total Clerk Shortages
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#ef4444", marginTop: 4 }}>
            {inr(grand.totalClerkShortage)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Total Excess: {inr(grand.totalClerkExcess)}
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #8b5cf6" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Delayed Bank Deposits
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#8b5cf6", marginTop: 4 }}>
            {grand.totalDelayedDeposits || 0}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Documented bank delays
          </div>
        </div>
      </div>

      {/* ── Cross-College Matrix Table ── */}
      <div className="card" style={{ padding: "1.25rem" }}>
        <h3 style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
          <FiActivity color="#003b67" /> Campus Cash &amp; Bank Reconciliation Matrix
        </h3>

        <div className="table-responsive">
          <table className="table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th>College / Campus</th>
                <th>Expected Cash</th>
                <th>Cash Handed Over</th>
                <th>Shortage / Excess</th>
                <th>In Accountant Vault</th>
                <th>Deposited in Bank</th>
                <th>Delays</th>
                <th>Audit Drilldown</th>
              </tr>
            </thead>
            <tbody>
              {colleges.map((c) => {
                const col = c.college || {};
                const hSum = c.handoverSummary || {};
                const dSum = c.depositSummary || {};
                const hasShortage = hSum.totalClerkShortage > 0;
                return (
                  <tr key={col._id || col.code}>
                    <td>
                      <div>
                        <strong>{col.name}</strong>
                      </div>
                      <span className="badge badge-info">{col.code}</span>
                    </td>
                    <td>{inr(hSum.totalExpectedCash)}</td>
                    <td style={{ fontWeight: 700 }}>{inr(hSum.totalActualHandedOver)}</td>
                    <td>
                      {hasShortage ? (
                        <span className="badge badge-danger">
                          Shortage: {inr(hSum.totalClerkShortage)}
                        </span>
                      ) : hSum.totalClerkExcess > 0 ? (
                        <span className="badge badge-warning">
                          Excess: +{inr(hSum.totalClerkExcess)}
                        </span>
                      ) : (
                        <span className="badge badge-success">Balanced</span>
                      )}
                    </td>
                    <td style={{ fontWeight: 700, color: "#f59e0b" }}>{inr(c.cashInAccountantVault)}</td>
                    <td style={{ fontWeight: 700, color: "#16a34a" }}>{inr(dSum.totalBankDeposited)}</td>
                    <td>
                      {dSum.totalDelayedDeposits > 0 ? (
                        <span className="badge badge-warning">{dSum.totalDelayedDeposits} Delayed</span>
                      ) : (
                        <span className="badge badge-success">0 Delays</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-outline"
                        onClick={() => openCollegeDrilldown(c)}
                        style={{ padding: "0.25rem 0.6rem", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}
                      >
                        <FiEye size={12} /> Drilldown
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── DRILLDOWN MODAL ── */}
      {selectedCollege && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "1rem",
          }}
        >
          <div
            className="card"
            style={{
              width: "100%",
              maxWidth: 820,
              maxHeight: "92vh",
              overflowY: "auto",
              padding: "1.75rem",
              background: "#fff",
              borderRadius: 10,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                  Drilldown Audit: {selectedCollege.college?.name} [{selectedCollege.college?.code}]
                </h3>
                <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0" }}>
                  Itemized clerk handovers and bank deposit slips.
                </p>
              </div>
              <button
                className="btn btn-outline"
                onClick={() => setSelectedCollege(null)}
                style={{ padding: "0.25rem 0.5rem" }}
              >
                <FiX size={14} />
              </button>
            </div>

            {drilldownLoading ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>Loading drilldown details...</div>
            ) : (
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                  Clerk Handovers ({drilldownHandovers.length})
                </h4>
                <div className="table-responsive" style={{ maxHeight: 250, overflowY: "auto", marginBottom: 16 }}>
                  <table className="table" style={{ width: "100%", fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Handover No</th>
                        <th>Clerk</th>
                        <th>Date</th>
                        <th>Expected</th>
                        <th>Actual</th>
                        <th>Difference</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldownHandovers.map((h) => (
                        <tr key={h._id}>
                          <td>{h.handoverNo}</td>
                          <td>{h.clerk?.displayName || h.clerk?.username}</td>
                          <td>{h.collectionDate}</td>
                          <td>{inr(h.expectedAmount)}</td>
                          <td style={{ fontWeight: 700 }}>{inr(h.actualAmount)}</td>
                          <td>
                            {h.difference === 0 ? (
                              <span className="badge badge-success">₹0</span>
                            ) : h.difference < 0 ? (
                              <span className="badge badge-danger">-₹{Math.abs(h.difference)}</span>
                            ) : (
                              <span className="badge badge-warning">+₹{h.difference}</span>
                            )}
                          </td>
                          <td>{h.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                  Bank Deposits ({drilldownDeposits.length})
                </h4>
                <div className="table-responsive" style={{ maxHeight: 250, overflowY: "auto" }}>
                  <table className="table" style={{ width: "100%", fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Deposit No</th>
                        <th>Bank &amp; Account</th>
                        <th>Date</th>
                        <th>Challan No</th>
                        <th>Amount Deposited</th>
                        <th>Delay Info</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drilldownDeposits.map((d) => (
                        <tr key={d._id}>
                          <td>{d.depositNo}</td>
                          <td>{d.bankName}</td>
                          <td>{new Date(d.depositDate).toLocaleDateString("en-IN")}</td>
                          <td>{d.challanSlipNo}</td>
                          <td style={{ fontWeight: 700 }}>{inr(d.amountDeposited)}</td>
                          <td>{d.isDelayed ? `Delayed (${d.delayDays}d): ${d.delayReason}` : "On-Time"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
